import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
Box,
CircularProgress,
Grid,
Stack,
Typography,
Alert,
IconButton,
Button,
} from '@mui/material';
import {
CheckCircle,
Cancel,
Lightbulb,
TrendingUp,
ArrowBack,
PictureAsPdf,
Star,
FiberManualRecord,
} from '@mui/icons-material';
import { getJobFitAnalysis } from '../../../services/CandidatService';
import { renderText, buildPdfHtml } from './jobFitAnalysisPdf';
import {
PageWrapper,
Inner,
HeaderCard,
SummaryCard,
StrengthCard,
GapCard,
CoachingCard,
ItemRow,
TrainingItem,
AccentBar,
} from './JobFitAnalysisPage.styles';
import PdfPreviewDialog from './PdfPreviewDialog';

export default function JobFitAnalysisPage() {
const { candidatId, jobId } = useParams();
const history = useHistory();

const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [data, setData] = useState(null);
const [exporting, setExporting] = useState(false);
const [previewOpen, setPreviewOpen] = useState(false);
const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

const handleExportPdf = async () => {
if (!data?.analysis) return;
setExporting(true);
try {
const html2pdf = (await import('html2pdf.js')).default;
const htmlString = buildPdfHtml(data.analysis);
const opt = {
margin: [10, 10, 10, 10],
filename: 'analyse-compatibilite.pdf',
image: { type: 'jpeg', quality: 0.98 },
html2canvas: { scale: 2, useCORS: true, logging: false, allowTaint: true, windowWidth: 794, width: 794 },
jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
pagebreak: { mode: ['css'] },
};

const blob = await html2pdf().set(opt).from(htmlString).output('blob');
const url = URL.createObjectURL(blob);
setPdfPreviewUrl(url);
setPreviewOpen(true);
} catch (e) {
console.error('Erreur export PDF :', e);
} finally {
setExporting(false);
}
};

const handleDownloadPdf = () => {
if (!pdfPreviewUrl) return;
const link = document.createElement('a');
link.href = pdfPreviewUrl;
link.download = 'analyse-compatibilite.pdf';
link.click();
};

const handleClosePreview = () => {
setPreviewOpen(false);
if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
setPdfPreviewUrl(null);
};

useEffect(() => {
if (!candidatId || !jobId) return;
setLoading(true);
setError(null);
getJobFitAnalysis(candidatId, jobId)
.then((res) => setData(res.data))
.catch((err) => {
console.error('Error fetching job fit analysis:', err);
setError(
err.response?.status === 404
? "L'analyse n'est pas disponible pour ce candidat ou cette offre."
: "Impossible de charger l'analyse de compatibilité. Veuillez réessayer plus tard."
);
})
.finally(() => setLoading(false));
}, [candidatId, jobId]);

return (
<PageWrapper>
<Inner>

<HeaderCard elevation={0}>
<Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
<IconButton
onClick={() => history.goBack()}
sx={{ bgcolor: 'rgba(255,255,255,.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,.25)' } }}
>
<ArrowBack />
</IconButton>
<Box>
<Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
Analyse de Compatibilité
</Typography>
<Typography variant="body2" sx={{ color: 'rgba(255,255,255,.72)', mt: 0.4 }}>
Comparaison détaillée entre le profil du candidat et l'offre d'emploi
</Typography>
</Box>
</Stack>
<Button
variant="contained"
startIcon={<PictureAsPdf />}
onClick={handleExportPdf}
disabled={exporting || !data}
sx={{
bgcolor: 'rgba(255,255,255,.18)',
color: '#fff',
border: '1.5px solid rgba(255,255,255,.35)',
backdropFilter: 'blur(6px)',
textTransform: 'none',
borderRadius: 10,
px: 3,
fontWeight: 600,
whiteSpace: 'nowrap',
'&:hover': { bgcolor: 'rgba(255,255,255,.28)' },
'&.Mui-disabled': { color: 'rgba(255,255,255,.4)' },
}}
>
{exporting ? 'Export en cours…' : 'Télécharger PDF'}
</Button>
</HeaderCard>

{loading && (
<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
<CircularProgress size={44} sx={{ color: '#117aa7' }} />
<Typography sx={{ ml: 2, color: '#64748b', fontWeight: 500 }}>
Génération de l'analyse par l'IA en cours…
</Typography>
</Box>
)}

{error && !loading && (
<Alert severity="error" variant="outlined" sx={{ borderRadius: 3, mt: 2 }}>
{error}
</Alert>
)}

{data && data.analysis && !loading && (
<>

<SummaryCard elevation={0}>
<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
<AccentBar color="#117aa7" />
<TrendingUp sx={{ color: '#117aa7', fontSize: 24 }} />
<Typography variant="h6" sx={{ fontWeight: 700, color: '#0d5a7a' }}>
Synthèse de Compatibilité
</Typography>
</Stack>
<Typography sx={{ color: '#475569', lineHeight: 1.75, fontSize: '0.96rem' }}>
{renderText(data.analysis.summary)}
</Typography>
</SummaryCard>

<Grid container spacing={2.5} sx={{ mb: 0 }}>
<Grid item xs={12} md={6}>
<StrengthCard elevation={0}>
<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
<AccentBar color="#22c55e" />
<CheckCircle sx={{ color: '#22c55e', fontSize: 22 }} />
<Typography sx={{ fontWeight: 700, color: '#166534', fontSize: '0.97rem' }}>
Points Forts pour ce Poste
</Typography>
</Stack>
<Stack spacing={1.25}>
{data.analysis.strong_points?.length > 0 ? (
data.analysis.strong_points.map((p, i) => (
<ItemRow key={i}>
<Star sx={{ color: '#22c55e', fontSize: 17, mt: 0.3, flexShrink: 0 }} />
<Typography sx={{ color: '#166534', lineHeight: 1.65, fontSize: '0.9rem' }}>
{renderText(p)}
</Typography>
</ItemRow>
))
) : (
<Typography sx={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.9rem' }}>
Aucun point fort spécifique extrait.
</Typography>
)}
</Stack>
</StrengthCard>
</Grid>

<Grid item xs={12} md={6}>
<GapCard elevation={0}>
<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
<AccentBar color="#ef4444" />
<Cancel sx={{ color: '#ef4444', fontSize: 22 }} />
<Typography sx={{ fontWeight: 700, color: '#991b1b', fontSize: '0.97rem' }}>
Lacunes Identifiées
</Typography>
</Stack>
<Stack spacing={1.25}>
{data.analysis.gaps?.length > 0 ? (
data.analysis.gaps.map((g, i) => (
<ItemRow key={i}>
<FiberManualRecord sx={{ color: '#ef4444', fontSize: 11, mt: 0.55, flexShrink: 0 }} />
<Typography sx={{ color: '#991b1b', lineHeight: 1.65, fontSize: '0.9rem' }}>
{renderText(g)}
</Typography>
</ItemRow>
))
) : (
<Typography sx={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.9rem' }}>
Aucune lacune majeure détectée.
</Typography>
)}
</Stack>
</GapCard>
</Grid>
</Grid>

<CoachingCard elevation={0}>
<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
<AccentBar color="#f59e0b" />
<Box sx={{ p: 0.75, bgcolor: '#f59e0b', borderRadius: '50%', display: 'flex', color: 'white' }}>
<Lightbulb sx={{ fontSize: 18 }} />
</Box>
<Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.97rem' }}>
Formations et Actions Recommandées
</Typography>
</Stack>
<Grid container spacing={1.75}>
{data.analysis.recommended_trainings?.length > 0 ? (
data.analysis.recommended_trainings.map((item, i) => (
<Grid item xs={12} md={6} key={i}>
<TrainingItem>
<Box
sx={{
minWidth: 24, height: 24, bgcolor: '#f59e0b', borderRadius: '50%',
display: 'flex', alignItems: 'center', justifyContent: 'center',
color: 'white', fontWeight: 800, fontSize: '0.78rem', flexShrink: 0,
}}
>
{i + 1}
</Box>
<Typography sx={{ color: '#78350f', lineHeight: 1.65, fontSize: '0.9rem' }}>
{renderText(item)}
</Typography>
</TrainingItem>
</Grid>
))
) : (
<Grid item xs={12}>
<Typography sx={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.9rem' }}>
Aucune formation recommandée.
</Typography>
</Grid>
)}
</Grid>
</CoachingCard>
</>
)}
</Inner>

<PdfPreviewDialog
open={previewOpen}
onClose={handleClosePreview}
pdfPreviewUrl={pdfPreviewUrl}
onDownload={handleDownloadPdf}
/>
</PageWrapper>
);
}
